import {
  canIncreaseAudienceIdentityPrivacy,
  normalizeAudienceIdentityPolicy,
  type AudienceIdentityMode,
  type AudienceIdentityPolicy,
  type AudienceIdentityPolicySource,
} from "../../shared/audienceIdentity.ts";
import type { AudienceIdentityTransition } from "../../shared/types.ts";

export const audienceIdentityModeOptions: ReadonlyArray<{
  value: AudienceIdentityMode;
  label: string;
  description: string;
}> = [
  {
    value: "public_name",
    label: "Names visible",
    description: "Participants enter a name, and participants, projector screens, moderators, and operators can see it.",
  },
  {
    value: "staff_identified",
    label: "Names private to staff",
    description: "Participants enter a name. Only staff (moderators and operators) see it; other participants and projector screens see a random identity.",
  },
  {
    value: "fully_anonymous",
    label: "Fully anonymous",
    description: "Names are not collected. Everyone sees only a random identity.",
  },
];

export function isAudienceIdentityModeSelectable(
  current: AudienceIdentityMode,
  next: AudienceIdentityMode,
): boolean {
  return canIncreaseAudienceIdentityPrivacy(current, next);
}

export function describeAudiencePrivacyForStaff(
  source: AudienceIdentityPolicy | AudienceIdentityPolicySource,
): { label: string; detail: string } {
  const policy = normalizeAudienceIdentityPolicy(source);
  const publicLabels = policy.publicAuthorDisplay === "hidden"
    ? "public labels hidden"
    : policy.identityMode === "public_name"
      ? "names public"
      : "random identities public";

  if (policy.identityMode === "fully_anonymous") {
    return {
      label: `Fully anonymous · ${publicLabels}`,
      detail: policy.publicAuthorDisplay === "hidden"
        ? "No names are collected. Participant and projector screens omit author labels."
        : "No names are collected. Every surface sees random identities.",
    };
  }

  if (policy.identityMode === "staff_identified") {
    return {
      label: `Names private to staff · ${publicLabels}`,
      detail: policy.publicAuthorDisplay === "hidden"
        ? "Staff see entered names. Participant and projector screens omit author labels."
        : "Staff see entered names. Participant and projector screens see random identities.",
    };
  }

  return {
    label: `Names visible · ${publicLabels}`,
    detail: policy.publicAuthorDisplay === "hidden"
      ? "Staff see entered names. Participant and projector screens omit author labels."
      : "Entered names are visible on participant, projector, moderator, and operator screens.",
  };
}

export function describeIdentityTransition(
  transition: AudienceIdentityTransition,
): { label: string; detail: string; retryable: boolean } {
  if (transition.status === "completed") {
    return {
      label: "Stored names erased",
      detail: `${transition.processedCount} records processed. The session is fully anonymous.`,
      retryable: false,
    };
  }
  if (transition.status === "failed") {
    return {
      label: `Name erasure paused · ${transition.phase}`,
      detail: transition.failureSummary ?? `${transition.processedCount} records processed before the conversion paused.`,
      retryable: true,
    };
  }
  return {
    label: `Erasing stored names · ${transition.phase}`,
    detail: `${transition.processedCount} records processed. Public responses are already fully anonymous.`,
    retryable: false,
  };
}
